import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, File, FileCode2, FileJson2, FilePlus2, Folder, FolderOpen, Search, Trash2 } from "lucide-react";
import type { WorkspaceFile } from "../types";
import { fileExtension } from "../lib/path";

interface TreeNode { name: string; path: string; type: "directory" | "file"; file?: WorkspaceFile; children: TreeNode[]; }

function buildTree(files: WorkspaceFile[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "", type: "directory", children: [] };
  for (const file of files) {
    const parts = file.path.split("/");
    let parent = root;
    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join("/");
      const isFile = index === parts.length - 1;
      let node = parent.children.find((child) => child.name === part);
      if (!node) {
        node = { name: part, path, type: isFile ? "file" : "directory", file: isFile ? file : undefined, children: [] };
        parent.children.push(node);
      }
      parent = node;
    });
  }
  const sort = (nodes: TreeNode[]): TreeNode[] => nodes.sort((a, b) => a.type === b.type ? a.name.localeCompare(b.name) : a.type === "directory" ? -1 : 1).map((node): TreeNode => ({ ...node, children: sort(node.children) }));
  return sort(root.children);
}

function FileIcon({ path }: { path: string }) {
  const extension = fileExtension(path);
  if (["json", "jsonc"].includes(extension)) return <FileJson2 size={14} />;
  if (["js", "jsx", "ts", "tsx", "html", "css", "py", "rs", "go"].includes(extension)) return <FileCode2 size={14} />;
  return <File size={14} />;
}

function TreeItem({ node, depth, selectedPath, onSelect, onDelete }: { node: TreeNode; depth: number; selectedPath: string | null; onSelect: (path: string) => void; onDelete: (path: string) => void }) {
  const [expanded, setExpanded] = useState(true);
  if (node.type === "directory") {
    return <><button className="tree-item directory" style={{ paddingLeft: 8 + depth * 13 }} onClick={() => setExpanded((value) => !value)}>
      {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{expanded ? <FolderOpen size={14} /> : <Folder size={14} />}<span>{node.name}</span>
    </button>{expanded && node.children.map((child) => <TreeItem key={child.path} node={child} depth={depth + 1} selectedPath={selectedPath} onSelect={onSelect} onDelete={onDelete} />)}</>;
  }
  return <div className={`tree-file-row ${selectedPath === node.path ? "active" : ""}`}>
    <button className="tree-item file" style={{ paddingLeft: 22 + depth * 13 }} onClick={() => onSelect(node.path)}><FileIcon path={node.path} /><span>{node.name}</span>{node.file?.kind === "binary" && <em>bin</em>}</button>
    <button className="tree-delete" onClick={() => onDelete(node.path)} aria-label={`Supprimer ${node.path}`}><Trash2 size={12} /></button>
  </div>;
}

export function FileTree({ files, selectedPath, onSelect, onNewFile, onDelete, footer }: {
  files: WorkspaceFile[]; selectedPath: string | null; onSelect: (path: string) => void; onNewFile: () => void; onDelete: (path: string) => void; footer?: ReactNode;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => query ? files.filter((file) => file.path.toLocaleLowerCase().includes(query.toLocaleLowerCase())) : files, [files, query]);
  const tree = useMemo(() => buildTree(filtered), [filtered]);
  return (
    <aside className="file-panel">
      <div className="file-panel-header"><span>Fichiers</span><button className="icon-button compact" onClick={onNewFile} aria-label="Nouveau fichier"><FilePlus2 size={15} /></button></div>
      <label className="file-search"><Search size={13} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher" /></label>
      <div className="tree" role="tree">{tree.length ? tree.map((node) => <TreeItem key={node.path} node={node} depth={0} selectedPath={selectedPath} onSelect={onSelect} onDelete={onDelete} />) : <p className="tree-empty">Aucun fichier</p>}</div>
      {footer && <div className="file-panel-footer">{footer}</div>}
    </aside>
  );
}
